/**
 * @fileoverview Component for displaying a regular button.
 * This component includes a button with a white background and gray text.
 */

import { ButtonHTMLAttributes, ForwardedRef, forwardRef } from 'react'

/**
 * @interface RegularButtonProps
 * @description Props for the RegularButton component.
 * @property {ButtonHTMLAttributes<HTMLButtonElement>} props - The props for the component.
 * @property {ForwardedRef<HTMLButtonElement>} ref - The ref for the component.
 */
interface RegularButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    ref: ForwardedRef<HTMLButtonElement>
}

/**
 * @function classNames
 * @description Combines multiple class names into a single string.
 * @param {...string[]} classes - The class names to combine.
 * @returns {string} - A single string of class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

const baseState =
    'inline-flex w-auto items-center h-10 justify-center rounded-md px-4 py-2.5 text-sm font-medium shadow-sm transition-colors'
const hoverState = 'hover:bg-gray-100'
const focusState = 'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
const normalState = 'bg-white text-gray-900 border border-gray-300'
const disabledState = 'bg-gray-200 text-gray-500 cursor-not-allowed'

/**
 * @function RegularButton
 * @description Component for displaying a regular button.
 * @param {PropsWithChildren<ButtonHTMLAttributes<HTMLButtonElement>>} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the regular button.
 */
export const RegularButton = forwardRef<HTMLButtonElement, RegularButtonProps>(
    function RegularButton(props, ref) {
        return (
            <button
                {...props}
                ref={ref}
                className={classNames(
                    baseState,
                    props.className,
                    props.disabled ? disabledState : classNames(normalState, hoverState, focusState)
                )}
            >
                {props.children}
            </button>
        )
    }
)
